import React, { useEffect, useMemo, useRef, useState } from "react";
import useFetch from "../../hooks/fetchMSSQL-hook";

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const VISUAL_W = 100;
const VISUAL_H = 150;
const GRID_COL_W = 120;

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const sortByContainerNo = (a, b) => {
  const an = toNum(a.ContainerNo);
  const bn = toNum(b.ContainerNo);
  if (an !== null && bn !== null) return an - bn;
  return String(a.ContainerNo || "").localeCompare(String(b.ContainerNo || ""));
};

const normalizeHex = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  const hex = s.startsWith("#") ? s : `#${s}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) return hex;
  if (/^#[0-9a-fA-F]{6}$/.test(hex)) return hex;
  if (/^#[0-9a-fA-F]{8}$/.test(hex)) return hex;
  return null;
};

const minAlertTheme = (minPct, minimumProfitabilityRate) => {
  const threshold = Number.isFinite(Number(minimumProfitabilityRate))
    ? Number(minimumProfitabilityRate)
    : null;

  if (threshold !== null && minPct < threshold) {
    return { border: "#dc3545", fill: "#dc3545" };
  }

  if (minPct > 80) {
    return { border: "#28a745", fill: "#28a745" };
  }

  return { border: "#ffc107", fill: "#ffc107" };
};

const PctPill = ({ pct, borderColor, className = "", style = {} }) => {
  return (
    <div className={`ccPctPill ${className}`} style={{ borderColor, ...style }}>
      {pct}%
    </div>
  );
};

const ContainerVisual = ({ pct, borderColor, fillStyle }) => {
  const W = VISUAL_W;
  const H = VISUAL_H;

  const WIN_LEFT = 16;
  const WIN_RIGHT = 16;
  const WIN_TOP = 18;
  const WIN_H = 115;
  const WIN_R = 12;

  return (
    <div
      style={{
        width: W,
        height: H,
        borderRadius: 18,
        border: `2px solid ${borderColor}`,
        background: "#f7f7f7",
        position: "relative",
        overflow: "hidden",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.02)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 22,
          background: "#ededed",
          borderBottom: "1px solid #d7d7d7",
        }}
      />

      <div
        style={{
          position: "absolute",
          left: WIN_LEFT,
          right: WIN_RIGHT,
          top: WIN_TOP,
          height: WIN_H,
          borderRadius: WIN_R,
          border: "1px solid rgba(0,0,0,0.14)",
          background: "rgba(255,255,255,0.55)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: `${pct}%`,
            minHeight: pct > 0 ? 4 : 0,
            borderRadius: WIN_R,
            opacity: 0.95,
            border: "1px solid rgba(0,0,0,0.10)",
            boxSizing: "border-box",
            ...fillStyle,
          }}
        />
      </div>

      <div
        style={{
          position: "absolute",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: 0,
          width: 44,
          height: 14,
          borderRadius: "14px 14px 0 0",
          background: "#dedede",
          borderTop: "1px solid #cfcfcf",
        }}
      />
    </div>
  );
};

const CoffeeContainers = ({ machineId, refreshKey = 0 }) => {
  const { fetchMssqlApi } = useFetch();
  const [items, setItems] = useState([]);

  const fetchRef = useRef(fetchMssqlApi);

  useEffect(() => {
    fetchRef.current = fetchMssqlApi;
  }, [fetchMssqlApi]);

  useEffect(() => {
    if (!machineId) return;

    let cancelled = false;

    fetchRef.current(
      "machine-ingredients",
      { method: "GET", params: { machineId }, hideNotification: true },
      (data) => {
        if (cancelled) return;
        setItems(Array.isArray(data) ? data : []);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [machineId, refreshKey]);

  const sorted = useMemo(() => [...items].sort(sortByContainerNo), [items]);

  const withPct = useMemo(() => {
    return sorted.map((x) => {
      const qty = Number(x.Quantity ?? 0);
      const cap = Number(x.Capacity ?? 0);

      const pctRaw = Number.isFinite(Number(x.Percent))
        ? Number(x.Percent)
        : cap > 0
        ? (qty / cap) * 100
        : 0;

      const pct = clamp(Math.round(pctRaw), 0, 100);

      return {
        ...x,
        __pct: pct,
      };
    });
  }, [sorted]);

  const minMeta = useMemo(() => {
    if (!withPct.length) {
      return {
        minKey: null,
        minPct: 0,
        minimumProfitabilityRate: null,
      };
    }

    const minItem = withPct.reduce((min, cur) => (cur.__pct < min.__pct ? cur : min), withPct[0]);

    const minKey = `${minItem.ContainerNo}-${minItem.ProductId}`;

    const minimumProfitabilityRate =
      minItem.MinimumProfitabilityRate ??
      withPct[0]?.MinimumProfitabilityRate ??
      minItem.CriticalProductState ??
      withPct[0]?.CriticalProductState ??
      null;

    return {
      minKey,
      minPct: minItem.__pct,
      minimumProfitabilityRate,
    };
  }, [withPct]);

  if (!withPct.length) {
    return (
      <div className="text-muted text-center" style={{ fontSize: 20 }}>
        Brak danych
      </div>
    );
  }

  const alertTheme = minMeta.minKey
    ? minAlertTheme(minMeta.minPct, minMeta.minimumProfitabilityRate)
    : null;

  return (
    <div
      style={{
        width: "100%",
        borderRadius: 12,
        padding: 12,
        background: "#fff",
      }}
    >
      <style>{`
        .ccWrap {
          width: 100%;
          overflow-x: auto;
          overflow-y: hidden;
          padding: 0;
          box-sizing: border-box;
        }

        .ccGrid {
          display: grid;
          grid-template-columns: repeat(4, ${GRID_COL_W}px);
          gap: 10px 8px;
          justify-content: start;
          width: max-content;
          padding: 4px 2px;
        }

        .ccItem {
          width: 100%;
          max-width: ${GRID_COL_W}px;
          text-align: center;
        }

        .ccVisualWrap {
          width: ${VISUAL_W}px;
          height: ${VISUAL_H}px;
          margin: 0 auto;
          display: grid;
          place-items: center;
          contain: layout paint;
        }

        .ccVisual {
          grid-area: 1 / 1;
          width: ${VISUAL_W}px;
          height: ${VISUAL_H}px;
        }

        .ccPctPill {
          margin-top: 0;
          width: 58px;
          height: 26px;
          border: 2px solid black;
          border-radius: 999px;
          display: grid;
          place-items: center;
          font-weight: 900;
          font-size: 12px;
          background: #fff;
          color: #000;
        }

        .ccPctPill--mid {
          grid-area: 1 / 1;
          z-index: 10;
          pointer-events: none;
          box-shadow: 0 2px 0 rgba(0,0,0,0.08);
        }

        .ccLabel1 { margin-top: 8px; font-size: 12px; font-weight: 900; }
        .ccLabel2 { font-size: 12px; line-height: 14px; }
        .ccLabel3 { font-size: 11px; color: #666; }
      `}</style>

      <div className="ccWrap">
        <div className="ccGrid">
          {withPct.map((x) => {
            const itemKey = `${x.ContainerNo}-${x.ProductId}`;
            const isMin = itemKey === minMeta.minKey;

            const baseColor =
              normalizeHex(x.IngredientTypeColorKey) ||
              normalizeHex(x.ColorKey) ||
              "#6cb2eb";

            const borderColor = isMin ? alertTheme.border : "black";

            const fillStyle = isMin
              ? { backgroundColor: alertTheme.fill }
              : { backgroundColor: baseColor };

            const pct = x.__pct;

            return (
              <div
                key={itemKey}
                className="ccItem"
                title={`${x.Name}: ${x.Quantity}/${x.Capacity} ${x.Unit || ""} (${pct}%)${
                  isMin && minMeta.minimumProfitabilityRate !== null
                    ? ` | próg: ${minMeta.minimumProfitabilityRate}%`
                    : ""
                }`}
              >
                <div className="ccVisualWrap">
                  <div className="ccVisual">
                    <ContainerVisual pct={pct} borderColor={borderColor} fillStyle={fillStyle} />
                  </div>

                  <PctPill pct={pct} borderColor={borderColor} className="ccPctPill--mid" />
                </div>

                <div className="ccLabel1">{x.ContainerNo}</div>
                <div className="ccLabel2">{x.Name}</div>
                <div className="ccLabel3">
                  {x.Quantity}/{x.Capacity} {x.Unit || ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CoffeeContainers;